import { useState, useMemo } from 'react';
import { demoCanteenBills } from '@/mocks/demoData';
import type { CanteenBill } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';
import { generateCanteenBillPDF } from '@/utils/pdfUtils';

export default function CanteenHistory() {
  const { addToast } = useApp();
  const [bills, setBills] = useState<CanteenBill[]>(demoCanteenBills);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [showDetail, setShowDetail] = useState<CanteenBill | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  const filtered = useMemo(() => {
    return bills.filter((b) => {
      const q = search.toLowerCase();
      if (search && !b.bill_no.toLowerCase().includes(q) && !b.table_or_room.toLowerCase().includes(q)) return false;
      if (filterStatus !== 'all' && b.status !== filterStatus) return false;
      if (filterPayment !== 'all' && b.payment_method !== filterPayment) return false;
      return true;
    });
  }, [bills, search, filterStatus, filterPayment]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const todayTotal = bills.filter((b) => new Date(b.created_at).toDateString() === new Date().toDateString()).reduce((s, b) => s + b.total, 0);
  const pendingTotal = bills.filter((b) => b.status === 'pending').reduce((s, b) => s + b.total, 0);

  const handleDownloadBill = (bill: CanteenBill) => {
    generateCanteenBillPDF(bill);
    addToast('success', `Bill ${bill.bill_no} downloaded as PDF`);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">Bill History</h1>
        <p className="text-navy-500 mt-1">Daily bill history with search and filters.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Total Bills</div>
          <div className="text-2xl font-bold text-navy-800">{bills.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Today Sales</div>
          <div className="text-2xl font-bold text-navy-800">Rs. {todayTotal.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Pending Bills</div>
          <div className="text-2xl font-bold text-red-600">{bills.filter((b) => b.status === 'pending').length}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Pending Amount</div>
          <div className="text-2xl font-bold text-red-600">Rs. {pendingTotal.toLocaleString('en-IN')}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
              <input type="text" placeholder="Search bill number, table, or room..." value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} className="w-full pl-9 pr-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Status</label>
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Payment</label>
            <select value={filterPayment} onChange={(e) => { setFilterPayment(e.target.value); setCurrentPage(1); }} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
              <option value="room_charge">Room Charge</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Bill No</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Table/Room</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Items</th>
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Total</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Payment</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Status</th>
                <th className="text-center px-5 py-3 font-semibold text-navy-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((b) => (
                <tr key={b.id} className="border-t border-navy-100 hover:bg-navy-50/50 transition-colors">
                  <td className="px-5 py-3.5 font-medium text-navy-800">{b.bill_no}</td>
                  <td className="px-5 py-3.5 text-navy-600">{b.table_or_room}</td>
                  <td className="px-5 py-3.5 text-navy-500">{b.items.length} items</td>
                  <td className="px-5 py-3.5 text-right font-medium text-navy-800">Rs. {b.total.toLocaleString('en-IN')}</td>
                  <td className="px-5 py-3.5"><span className="text-xs text-navy-500 capitalize">{b.payment_method.replace('_', ' ')}</span></td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${b.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{b.status}</span>
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => setShowDetail(b)} className="text-navy-500 hover:text-navy-700 cursor-pointer"><i className="ri-eye-line" /></button>
                      <button onClick={() => handleDownloadBill(b)} className="text-navy-500 hover:text-green-600 cursor-pointer" title="Download bill PDF"><i className="ri-file-download-line" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-navy-100">
            <div className="text-xs text-navy-500">Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}</div>
            <div className="flex gap-1">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 border border-navy-200 rounded-lg text-sm disabled:opacity-40 hover:bg-navy-50 cursor-pointer"><i className="ri-arrow-left-s-line" /></button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setCurrentPage(p)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${currentPage === p ? 'bg-navy-700 text-white' : 'border border-navy-200 hover:bg-navy-50 text-navy-600'} cursor-pointer`}>{p}</button>
              ))}
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 border border-navy-200 rounded-lg text-sm disabled:opacity-40 hover:bg-navy-50 cursor-pointer"><i className="ri-arrow-right-s-line" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">{showDetail.bill_no}</h3>
              <button onClick={() => setShowDetail(null)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm text-navy-500">{showDetail.table_or_room}</div>
                  <div className="text-xs text-navy-400">{new Date(showDetail.created_at).toLocaleString('en-IN')}</div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${showDetail.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{showDetail.status}</span>
              </div>
              <div className="space-y-2 mb-4">
                {showDetail.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-navy-800">{it.qty}x {it.name}</span>
                    <span className="font-medium">Rs. {(it.price * it.qty).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-navy-100 pt-3 space-y-1">
                <div className="flex justify-between text-sm"><span className="text-navy-500">Subtotal</span><span>Rs. {showDetail.subtotal.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm"><span className="text-navy-500">GST</span><span>Rs. {showDetail.gst_total.toLocaleString('en-IN')}</span></div>
                {showDetail.discount > 0 && <div className="flex justify-between text-sm"><span className="text-navy-500">Discount</span><span className="text-green-600">-Rs. {(showDetail.discount_type === 'percent' ? showDetail.subtotal * showDetail.discount / 100 : showDetail.discount).toLocaleString('en-IN')}</span></div>}
                <div className="flex justify-between text-lg font-bold pt-2"><span className="text-navy-800">Total</span><span className="text-navy-800">Rs. {showDetail.total.toLocaleString('en-IN')}</span></div>
              </div>
              <div className="mt-4 p-3 bg-navy-50 rounded-lg text-center text-sm text-navy-600 capitalize">
                Payment: {showDetail.payment_method.replace('_', ' ')}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button
                onClick={() => handleDownloadBill(showDetail)}
                className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-download-line mr-2" /> Download Bill PDF
              </button>
              <button
                onClick={() => setShowDetail(null)}
                className="px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}